import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useCart } from '../store/cart';
import { money } from '../lib/format';
import { ProductThumb } from './ProductThumb';

export function CartDrawer({
  open,
  onClose,
  qrToken,
}: {
  open: boolean;
  onClose: () => void;
  qrToken?: string;
}) {
  const navigate = useNavigate();
  const { lines, setQty, remove, total } = useCart();

  return (
    <>
      <div
        className={clsx(
          'fixed inset-0 z-40 bg-ink-900/40 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
      />
      <aside
        className={clsx(
          'fixed inset-y-0 right-0 z-50 flex w-[88%] max-w-sm flex-col bg-white shadow-xl transition-transform',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
          <h2 className="text-base font-semibold">Your selection</h2>
          <button onClick={onClose} className="text-ink-400" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {lines.length === 0 ? (
            <p className="py-16 text-center text-sm text-ink-400">Your cart is empty.</p>
          ) : (
            <ul className="space-y-3">
              {lines.map((l) => (
                <li key={l.assignmentId} className="flex gap-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-ink-50">
                    <ProductThumb src={l.image} alt={l.name} label={l.photoId} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-sm font-medium text-ink-900">{l.name}</p>
                    <p className="text-[11px] text-ink-400">
                      #{l.barcodeNo} · Rack {l.rackNumber}
                    </p>
                    <div className="mt-1 flex items-center justify-between">
                      <div className="flex items-center rounded-md border border-ink-200 text-sm">
                        <button className="px-2 py-0.5" onClick={() => setQty(l.assignmentId, l.quantity - 1)}>
                          −
                        </button>
                        <span className="w-6 text-center">{l.quantity}</span>
                        <button className="px-2 py-0.5" onClick={() => setQty(l.assignmentId, l.quantity + 1)}>
                          +
                        </button>
                      </div>
                      <span className="text-sm font-semibold text-gold-600">
                        {l.price ? money(Number(l.price) * l.quantity) : '—'}
                      </span>
                    </div>
                    <button
                      className="mt-1 text-[11px] text-ink-400 underline"
                      onClick={() => remove(l.assignmentId)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-ink-100 p-4">
          <div className="mb-3 flex items-center justify-between text-sm">
            <span className="text-ink-500">Estimated total</span>
            <span className="text-lg font-semibold text-ink-900">{money(total())}</span>
          </div>
          <button
            className="btn-gold w-full"
            disabled={lines.length === 0}
            onClick={() => {
              onClose();
              navigate(`/r/${qrToken}/checkout`);
            }}
          >
            Proceed to booking
          </button>
          <p className="mt-2 text-center text-[11px] text-ink-300">
            Final pricing is confirmed by our staff.
          </p>
        </div>
      </aside>
    </>
  );
}
